import React, { useState, useEffect } from 'react';

const EventModal = ({ isOpen, onClose, onSave, onDelete, eventData, userColor, setModalMessage }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [allDay, setAllDay] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [notes, setNotes] = useState('');
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('details');
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (eventData) {
            setTitle(eventData.title || '');
            setDescription(eventData.description || '');
            setAllDay(eventData.allDay || false);
            setStartDate(eventData.startDate || '');
            setEndDate(eventData.endDate || '');
            setStartTime(eventData.startTime || '');
            setEndTime(eventData.endTime || '');
            setNotes(eventData.notes || '');
        } else {
            setTitle('');
            setDescription('');
            setAllDay(false);
            setStartDate('');
            setEndDate('');
            setStartTime('');
            setEndTime('');
            setNotes('');
        }
        setError('');
        setActiveTab('details');
    }, [eventData, isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsAnimating(true);

        if (!title.trim()) {
            setError('Event title is required.');
            setIsAnimating(false);
            return;
        }
        if (!startDate) {
            setError('Start date is required.');
            setIsAnimating(false);
            return;
        }
        if (!allDay && (!startTime || !endTime)) {
            setError('Start and end times are required for non-all-day events.');
            setIsAnimating(false);
            return;
        }
        if (new Date(startDate) > new Date(endDate) && endDate) {
            setError('End date cannot be before start date.');
            setIsAnimating(false);
            return;
        }
        if (!allDay && startTime && endTime && startTime >= endTime && startDate === endDate) {
            setError('End time must be after start time on the same day.');
            setIsAnimating(false);
            return;
        }

        // Simulate save animation
        await new Promise(resolve => setTimeout(resolve, 1000));

        onSave({
            id: eventData?.id,
            title: title.trim(),
            description: description.trim(),
            allDay: allDay,
            startDate: startDate,
            endDate: endDate || startDate,
            startTime: allDay ? '' : startTime,
            endTime: allDay ? '' : endTime,
            notes: notes.trim(),
            color: userColor || '#4d9eff'
        });
        
        setIsAnimating(false);
        onClose();
    };

    const handleDelete = async () => {
        setIsAnimating(true);
        await new Promise(resolve => setTimeout(resolve, 800));
        onDelete(eventData.id);
        setIsAnimating(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
            {/* Animated Background Elements */}
            <div className="absolute inset-0">
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full blur-3xl animate-float"></div>
                <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-gradient-to-tr from-cyan-500/20 to-pink-500/20 rounded-full blur-3xl animate-bounce-slow"></div>
            </div>

            <div className="relative glass-card rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/20 animate-slide-up">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/10"></div>
                
                {/* Header */}
                <div className="relative z-10 p-8 border-b border-white/10">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="relative group">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-lg opacity-60 group-hover:opacity-80 transition-opacity animate-pulse"></div>
                                <div className="relative w-12 h-12 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/20">
                                    <i className="fas fa-calendar-plus text-xl text-white animate-float"></i>
                                </div>
                            </div>
                            <div className="ml-4">
                                <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent animate-gradient">
                                    {eventData ? 'Edit Event' : 'Create New Event'}
                                </h3>
                                <p className="text-gray-400 mt-1">Plan your schedule with style</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rotate-90"
                        >
                            <i className="fas fa-times text-gray-300"></i>
                        </button>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="relative z-10 flex border-b border-white/10 bg-white/5">
                    <button
                        onClick={() => setActiveTab('details')}
                        className={`flex-1 py-4 px-6 text-sm font-semibold transition-all duration-300 relative ${
                            activeTab === 'details' 
                            ? 'text-blue-400 bg-blue-500/10' 
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                    >
                        <i className="fas fa-info-circle mr-2"></i>
                        Event Details
                        {activeTab === 'details' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 animate-slide-in"></div>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('timing')}
                        className={`flex-1 py-4 px-6 text-sm font-semibold transition-all duration-300 relative ${
                            activeTab === 'timing' 
                            ? 'text-purple-400 bg-purple-500/10' 
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                    >
                        <i className="fas fa-clock mr-2"></i>
                        Date & Time
                        {activeTab === 'timing' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-cyan-500 animate-slide-in"></div>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('notes')}
                        className={`flex-1 py-4 px-6 text-sm font-semibold transition-all duration-300 relative ${
                            activeTab === 'notes' 
                            ? 'text-cyan-400 bg-cyan-500/10' 
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                    >
                        <i className="fas fa-sticky-note mr-2"></i>
                        Notes
                        {activeTab === 'notes' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-pink-500 animate-slide-in"></div>
                        )}
                    </button>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="relative z-10 overflow-y-auto max-h-[60vh] custom-scrollbar">
                    {error && (
                        <div className="mx-8 mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl animate-shake">
                            <p className="text-red-400 flex items-center">
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                {error}
                            </p>
                        </div>
                    )}

                    <div className="p-8 space-y-6">
                        {/* Details Tab */}
                        {activeTab === 'details' && (
                            <div className="space-y-6 animate-slide-in">
                                <div className="group">
                                    <label className="block text-sm font-bold text-gray-300 mb-3 flex items-center">
                                        <i className="fas fa-tag mr-2 text-blue-400"></i>
                                        Event Title <span className="text-red-400 ml-1">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            className="w-full py-4 px-6 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-400/50 focus:bg-white/10 transition-all duration-300 hover:bg-white/8 pr-12"
                                            placeholder="e.g., Team Meeting, Project Deadline"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            required
                                        />
                                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                            <i className="fas fa-pen text-gray-400 group-hover:text-blue-400 transition-colors"></i>
                                        </div>
                                    </div>
                                </div>

                                <div className="group">
                                    <label className="block text-sm font-bold text-gray-300 mb-3 flex items-center">
                                        <i className="fas fa-align-left mr-2 text-purple-400"></i>
                                        Description
                                    </label>
                                    <div className="relative">
                                        <textarea
                                            className="w-full py-4 px-6 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-400/50 focus:bg-white/10 transition-all duration-300 hover:bg-white/8 resize-none h-32"
                                            placeholder="A brief description of the event"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                        />
                                        <div className="absolute right-4 top-4">
                                            <i className="fas fa-edit text-gray-400 group-hover:text-purple-400 transition-colors"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Timing Tab */}
                        {activeTab === 'timing' && (
                            <div className="space-y-6 animate-slide-in">
                                <div className="glass-card rounded-2xl p-6 border border-white/10">
                                    <div className="flex items-center justify-between mb-6">
                                        <label className="text-sm font-bold text-gray-300 flex items-center">
                                            <i className="fas fa-sun mr-2 text-yellow-400"></i>
                                            All Day Event
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                id="allDay"
                                                className="sr-only"
                                                checked={allDay}
                                                onChange={(e) => setAllDay(e.target.checked)}
                                            />
                                            <label
                                                htmlFor="allDay"
                                                className={`block w-12 h-6 rounded-full cursor-pointer transition-all duration-300 ${
                                                    allDay ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 'bg-gray-600'
                                                }`}
                                            >
                                                <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                                                    allDay ? 'translate-x-6' : 'translate-x-0.5'
                                                } mt-0.5`}></div>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="group">
                                        <label className="block text-sm font-bold text-gray-300 mb-3 flex items-center">
                                            <i className="fas fa-calendar-day mr-2 text-green-400"></i>
                                            Start Date <span className="text-red-400 ml-1">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className="w-full py-4 px-6 bg-white/5 border border-white/10 rounded-2xl text-white focus:outline-none focus:border-green-400/50 focus:bg-white/10 transition-all duration-300 hover:bg-white/8"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="group">
                                        <label className="block text-sm font-bold text-gray-300 mb-3 flex items-center">
                                            <i className="fas fa-calendar-day mr-2 text-red-400"></i>
                                            End Date
                                        </label>
                                        <input
                                            type="date"
                                            className="w-full py-4 px-6 bg-white/5 border border-white/10 rounded-2xl text-white focus:outline-none focus:border-red-400/50 focus:bg-white/10 transition-all duration-300 hover:bg-white/8"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {!allDay && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-slide-in">
                                        <div className="group">
                                            <label className="block text-sm font-bold text-gray-300 mb-3 flex items-center">
                                                <i className="fas fa-clock mr-2 text-cyan-400"></i>
                                                Start Time <span className="text-red-400 ml-1">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                className="w-full py-4 px-6 bg-white/5 border border-white/10 rounded-2xl text-white focus:outline-none focus:border-cyan-400/50 focus:bg-white/10 transition-all duration-300 hover:bg-white/8"
                                                value={startTime}
                                                onChange={(e) => setStartTime(e.target.value)}
                                                required={!allDay}
                                            />
                                        </div>

                                        <div className="group">
                                            <label className="block text-sm font-bold text-gray-300 mb-3 flex items-center">
                                                <i className="fas fa-clock mr-2 text-pink-400"></i>
                                                End Time <span className="text-red-400 ml-1">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                className="w-full py-4 px-6 bg-white/5 border border-white/10 rounded-2xl text-white focus:outline-none focus:border-pink-400/50 focus:bg-white/10 transition-all duration-300 hover:bg-white/8"
                                                value={endTime}
                                                onChange={(e) => setEndTime(e.target.value)}
                                                required={!allDay}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Notes Tab */}
                        {activeTab === 'notes' && (
                            <div className="space-y-6 animate-slide-in">
                                <div className="group">
                                    <label className="block text-sm font-bold text-gray-300 mb-3 flex items-center">
                                        <i className="fas fa-sticky-note mr-2 text-cyan-400"></i>
                                        Additional Notes
                                    </label>
                                    <div className="relative">
                                        <textarea
                                            className="w-full py-4 px-6 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400/50 focus:bg-white/10 transition-all duration-300 hover:bg-white/8 resize-none h-48"
                                            placeholder="Add detailed notes, use bullet points for lists..."
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                        />
                                        <div className="absolute right-4 top-4">
                                            <i className="fas fa-feather text-gray-400 group-hover:text-cyan-400 transition-colors"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </form>

                {/* Footer */}
                <div className="relative z-10 p-8 border-t border-white/10 bg-white/5">
                    <div className="flex justify-between items-center">
                        <div className="flex space-x-3">
                            {eventData && (
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    disabled={isAnimating}
                                    className="relative group overflow-hidden bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-2xl font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-red-500/25 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    <div className="relative flex items-center space-x-2">
                                        <i className={`fas ${isAnimating ? 'fa-spinner fa-spin' : 'fa-trash'}`}></i>
                                        <span>{isAnimating ? 'Deleting...' : 'Delete'}</span>
                                    </div>
                                </button>
                            )}
                        </div>

                        <div className="flex space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-8 py-3 bg-white/10 hover:bg-white/20 text-gray-300 rounded-2xl font-semibold transition-all duration-300 hover:scale-105 hover:-translate-y-0.5"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={isAnimating}
                                className="relative group overflow-hidden bg-gradient-to-r from-blue-500 to-purple-500 text-white px-8 py-3 rounded-2xl font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <div className="relative flex items-center space-x-2">
                                    <i className={`fas ${isAnimating ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                                    <span>{isAnimating ? 'Saving...' : 'Save Event'}</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventModal;
